import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography, 
  Paper,
  CircularProgress,
  Stack,
  Alert,
  Snackbar,
  TextField,
  Grid
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import { useDropzone } from 'react-dropzone';
import { translationUpload } from '../api/translationUpload';

const TranslationUploadForm = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [comicFile, setComicFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [title, setTitle] = useState('');
  const [volume, setVolume] = useState('1');
  const [seriesName, setSeriesName] = useState('Translation Series');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleComicDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setComicFile(acceptedFiles[0]);
      if (!title) {
        // Extract filename as default title
        const fileName = acceptedFiles[0].name.split('.')[0];
        setTitle(fileName);
      }
    }
  };

  const handleExcelDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setExcelFile(acceptedFiles[0]);
    }
  };

  const { getRootProps: getComicRootProps, getInputProps: getComicInputProps } = useDropzone({
    onDrop: handleComicDrop,
    accept: {
      'image/*': ['.jpeg', '.png', '.jpg'],
      'application/pdf': ['.pdf'],
      'application/photoshop': ['.psd', '.psb'],
    },
    maxFiles: 1,
  });

  const { getRootProps: getExcelRootProps, getInputProps: getExcelInputProps } = useDropzone({
    onDrop: handleExcelDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleSubmit = async () => {
    if (!comicFile) {
      setSnackbar({
        open: true,
        message: 'Comic file is required',
        severity: 'error'
      });
      return;
    }
    // Add size validation to prevent empty files
    if (comicFile.size === 0) {
      setSnackbar({
        open: true,
        message: 'Comic file appears to be empty',
        severity: 'error'
      });
      return;
    }
    setIsLoading(true);

    // Prepare metadata
    const metadata = {
      title: title || comicFile.name.split('.')[0],
      volume: volume || '1',
      series: seriesName || 'Translation Series'
    };

    try {
      const result = await translationUpload(comicFile, excelFile, metadata);
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Files uploaded successfully!',
          severity: 'success'
        });
        handleClose();
      } else {
        const errorMsg = result.data?.error || 'Upload failed. Please try again.';
        setSnackbar({
          open: true,
          message: errorMsg,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setSnackbar({
        open: true,
        message: `Upload failed: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setComicFile(null);
    setExcelFile(null);
    setTitle('');
    setVolume('');
    setSeriesName('');
    onClose();
  };

  const removeComicFile = () => {
    setComicFile(null);
  };

  const removeExcelFile = () => {
    setExcelFile(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Upload for Translation
            </Typography>
          </Box>
        </DialogTitle>
        
        {!isLoading ? (
          <DialogContent dividers>
            <Stack spacing={3}>
              {/* Comic metadata fields */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Title"
                    variant="outlined"
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter comic title"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Volume"
                    variant="outlined"
                    fullWidth
                    type="number"
                    inputProps={{ min: "1" }}
                    value={volume}
                    onChange={(e) => setVolume(e.target.value)}
                    placeholder="1"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Series Name"
                    variant="outlined"
                    fullWidth
                    value={seriesName}
                    onChange={(e) => setSeriesName(e.target.value)}
                    placeholder="Translation Series"
                  />
                </Grid>
              </Grid>
              
              {/* Comic file upload */}
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="500">
                  Upload Comic File *
                </Typography>
                <Paper
                  elevation={3}
                  {...getComicRootProps({ className: "dropzone" })}
                  sx={{
                    width: "100%",
                    minHeight: "15vh",
                    borderRadius: "10px",
                    padding: "0 2rem",
                    cursor: "pointer",
                    border: '1px dashed #ccc',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <input {...getComicInputProps()} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "2vw",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "75%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="h6" align="center">
                        {comicFile ? comicFile.name : "Drop or click to upload comic file"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        {comicFile ? `${(comicFile.size / 1024 / 1024).toFixed(2)} MB` : "Supported formats: .pdf, .png, .jpg, .jpeg, .psd, .psb"}
                      </Typography>
                    </div>
                    <div
                      style={{
                        width: "25%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {comicFile ? (
                        <Button 
                          variant="outlined" 
                          color="error" 
                          startIcon={<ClearIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeComicFile();
                          }}
                        >
                          Remove
                        </Button>
                      ) : (
                        <CloudUploadIcon fontSize="large" />
                      )}
                    </div>
                  </div>
                </Paper>
              </Box>

              {/* Excel file upload */}
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="500">
                  Upload Excel File *
                </Typography>
                <Paper
                  elevation={3}
                  {...getExcelRootProps({ className: "dropzone" })}
                  sx={{
                    width: "100%",
                    minHeight: "15vh",
                    borderRadius: "10px",
                    padding: "0 2rem",
                    cursor: "pointer",
                    border: '1px dashed #ccc',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <input {...getExcelInputProps()} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "2vw",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "75%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="h6" align="center">
                        {excelFile ? excelFile.name : "Drop or click to upload Excel file"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        {excelFile ? `${(excelFile.size / 1024 / 1024).toFixed(2)} MB` : "Supported formats: .xlsx, .xls, .csv"}
                      </Typography>
                    </div>
                    <div
                      style={{
                        width: "25%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {excelFile ? (
                        <Button 
                          variant="outlined" 
                          color="error" 
                          startIcon={<ClearIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeExcelFile();
                          }}
                        >
                          Remove
                        </Button>
                      ) : (
                        <CloudUploadIcon fontSize="large" />
                      )}
                    </div>
                  </div>
                </Paper>
              </Box>
            </Stack>
          </DialogContent>
        ) : (
          <DialogContent dividers>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "30vh",
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress />
                <Typography>Uploading Files...</Typography>
              </Stack>
            </Box>
          </DialogContent>
        )}
        
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={!comicFile || isLoading}
            startIcon={<CloudUploadIcon />}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TranslationUploadForm;
